import { inputVariants } from '@/common/components/Input'
import { Label } from '@/shad/ui/label'
import { useEffect, useState } from 'react'
import CurrencyInput from 'react-currency-input-field'
import { useTranslation } from 'react-i18next'
import FormErrorMessage from '../FormErrorMessage'

// Create a type that omits conflicting properties from CurrencyInputProps
type CurrencyInputOwnProps = {
  id: string
  value?: string | number
  onChange: (value: number) => void
  error?: string
  required?: boolean
  label?: string
  placeholder?: string
  className?: string
}

// Use Omit to avoid type conflicts with the original CurrencyInput props
export const CurrencyInputField = ({
  id,
  value,
  onChange,
  error,
  required = false,
  label,
  placeholder,
  className,
  ...currencyInputProps
}: CurrencyInputOwnProps & Omit<React.ComponentProps<typeof CurrencyInput>, 'onChange' | 'value' | 'id'>) => {
  const { t: translate } = useTranslation()
  const [inputValue, setInputValue] = useState(value)

  useEffect(() => {
    setInputValue(value)
  }, [value])
  
  const handleValueChange = (
    value: string | undefined,
    _: any,
    values: any
  ) => {
    setInputValue(value)
    onChange(values.float)
  }

  const handleBlur = () => {
    if (inputValue && inputValue !== '') {
      // Format with two decimal places on blur
      const numValue = parseFloat(String(inputValue).replace(/[$,]/g, ''))
      if (!isNaN(numValue)) {
        const formattedValue = numValue.toFixed(2)
        setInputValue(formattedValue)
        onChange(parseFloat(formattedValue))
      }
    }
  }

  return (
    <section
      className={`flex flex-col space-y-2 ${className}`}
      aria-labelledby={`${id}_label`}
    >
      {label && (
        <Label id={`${id}_label`} htmlFor={id}>
          {translate(label)}{' '}
          {required && <span className="text-danger ml-1">*</span>}
        </Label>
      )}
      <div className="relative mt-2 rounded-md shadow-sm">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <span className="text-gray-500 sm:text-sm">$</span>
        </div>
        <CurrencyInput
          id={id}
          className={inputVariants({
            variant: 'text',
          })}
          allowDecimals={true}
          decimalsLimit={2}
          prefix="$"
          value={inputValue}
          placeholder={placeholder}
          onValueChange={handleValueChange}
          onBlur={handleBlur}
          {...currencyInputProps}
        />
      </div>
      {error && <FormErrorMessage message={translate(error)} />}
    </section>
  )
}